import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  left: 0; top: 0; right: 0; bottom: 0;
  z-index: 3000;
  background: rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Stage = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
`;

const Pointer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 36px;
  z-index: 15;
  background: #ff9800;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
  ${props => props.side === 'left' ? `
    left: 0;
    clip-path: polygon(0% 0%, 0% 100%, 100% 50%);
  ` : `
    right: 0;
    clip-path: polygon(100% 0%, 100% 100%, 0% 50%);
  `}
`;

const Strip = styled.div`
  position: absolute;
  left: 0; right: 0;
  will-change: transform;
`;

const Item = styled.div`
  height: ${props => props.itemHeight}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 30px;
`;

const ItemImage = styled.img`
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  object-fit: contain;
`;

const ItemName = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #fff;
  margin-top: 12px;
  text-align: center;
`;

const ItemDesc = styled.div`
  font-size: 15px;
  color: #ccc;
  margin-top: 2px;
  text-align: center;
  word-break: break-word;
`;

function parseMarketHashName(marketHashName) {
	if (!marketHashName) return { name: '', desc: '' };
	const [name, ...rest] = marketHashName.split('|');
	return { name: (name || '').trim(), desc: rest.join('|').trim() };
}

export default function RouletteOverlay({
	items,
	winnerItem,
	durationMs = 9000,
	itemHeight = 300,
	repeats = 60,
	onComplete,
	start = false,
	slowPhaseRatio = 0.7, // доля времени для медленной фазы
	slowDistanceRatio = 0.6, // доля пути, проходимая в медленной фазе линейно
	travelLoops = 3, // Сколько полных проходов по списку проезжаем
	deterministic = false,
	targetIndex = 100,
	trackLength = 120,
}) {
	const stageRef = useRef(null);
	const stripRef = useRef(null);
	const [started, setStarted] = useState(false);

	// удалено: локальная версия поиска используется внутри эффекта

	const track = useMemo(() => {
		if (deterministic && winnerItem && items && items.length > 0) {
			const len = Math.max(targetIndex + 1, trackLength);
			const arr = new Array(len);
			for (let i = 0; i < len; i += 1) {
				if (i === targetIndex) arr[i] = winnerItem;
				else arr[i] = items[Math.floor(Math.random() * items.length)] || winnerItem;
			}
			return arr;
		}
		const arr = [];
		for (let r = 0; r < repeats; r += 1) arr.push(...items);
		return arr;
	}, [items, repeats, deterministic, winnerItem, targetIndex, trackLength]);

	useEffect(() => {
		if (!stageRef.current || !stripRef.current || !winnerItem || started || !start) return;
		setStarted(true);

		const centerY = (stageRef.current.clientHeight - itemHeight) / 2;
		const findIndexLocal = (weapon) => {
			if (!weapon) return 0;
			let idx = items.findIndex(i => i?.id && weapon?.id && String(i.id) === String(weapon.id));
			if (idx !== -1) return idx;
			idx = items.findIndex(i => i?.marketHashName && weapon?.marketHashName && i.marketHashName === weapon.marketHashName);
			if (idx !== -1) return idx;
			idx = items.findIndex(i => i?.iconUrl && weapon?.iconUrl && i.iconUrl === weapon.iconUrl);
			if (idx !== -1) return idx;
			return 0;
		};

		let endIndex;
		if (deterministic) {
			endIndex = targetIndex;
		} else {
			const winnerIdxInItems = findIndexLocal(winnerItem);
			// Контролируемая длина пробега фиксированным числом петель
			const loops = Math.max(1, Math.ceil(travelLoops));
			const baseOffset = items.length * loops;
			endIndex = baseOffset + winnerIdxInItems;
		}
		const targetY = -(endIndex * itemHeight - centerY);

		const startTs = performance.now();
		// Берём текущую позицию ленты, чтобы старт был непрерывным
		const getCurrentY = () => {
			try {
				const tr = getComputedStyle(stripRef.current).transform;
				if (tr && tr !== 'none') {
					const m = new DOMMatrix(tr);
					return m.m42 || 0;
				}
			} catch (_) { }
			return 0;
		};
		const startY = getCurrentY();

		// Единая ease-out кривая: быстро в начале, плавное замедление к концу
		const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

		const step = (now) => {
			const elapsed = now - startTs;
			const totalDuration = Math.max(1, durationMs);
			const p = Math.min(1, elapsed / totalDuration);
			const e = easeOutCubic(p);
			const y = startY + (targetY - startY) * e;
			stripRef.current.style.transform = `translate3d(0, ${y}px, 0)`;
			if (p < 1) {
				requestAnimationFrame(step);
			} else {
				stripRef.current.style.transform = `translate3d(0, ${targetY}px, 0)`;
				if (onComplete) onComplete();
			}
		};

		requestAnimationFrame(step);
	}, [durationMs, itemHeight, items, repeats, winnerItem, started, start, onComplete, travelLoops, deterministic, targetIndex, trackLength]);

	return (
		<Overlay>
			<Stage ref={stageRef}>
				<Pointer side="left" />
				<Pointer side="right" />
				<Strip ref={stripRef}>
					{track.map((weapon, i) => {
						const { name, desc } = parseMarketHashName(weapon.marketHashName);
						return (
							<Item key={`${i}-${weapon.id || weapon.marketHashName || i}`} itemHeight={itemHeight}>
								<ItemImage src={weapon.iconUrl} alt={weapon.marketHashName} />
								<ItemName>{name}</ItemName>
								<ItemDesc>{desc}</ItemDesc>
							</Item>
						);
					})}
				</Strip>
			</Stage>
		</Overlay>
	);
}


